export const LEARN_CONTENT = [
  {
    slug: "prop-firm-trading-journal",
    title: "The Ultimate Guide to Prop Firm Trading Journaling",
    category: "Prop Firm Mastery",
    accent: "#38bdf8",
    description: "Learn how to track drawdown and consistency scores to protect your funded accounts.",
    ctaTitle: "Ready to protect your funded account?",
    ctaSubtitle: "Start journaling with rules, not hope. Join the disciplined 5%.",
    content: `
      <div class="who-is-this-for">
        <strong>This guide is for traders who:</strong>
        <ul>
          <li>Manage or aspire to manage institutional capital</li>
          <li>Are struggling with daily drawdown breaches</li>
          <li>Want to move from 'gambling' to professional consistency</li>
        </ul>
      </div>

      <h2>Why \"General\" Journals Lead to Breached Accounts</h2>
      <p>Trading for a Prop Firm is not like trading a personal account. You aren't just fighting the market; you are fighting a strict set of mathematical rules. To keep your funded status, your journaling must go beyond simple \"Profit and Loss\" tracking.</p>
      
      <h3>1. Tracking Max Drawdown</h3>
      <p>Daily drawdown limit is the #1 reason accounts are lost. Standard journals tell you what you lost <em>after</em> the day is over. By then, the account is already gone.</p>
      <div class="pro-tip">
        <strong>The Solution:</strong> Your journal must track equity in real-time. We emphasize the 'Distance to Breach'—the exact dollar amount you can lose before the firm pulls the plug.
      </div>

      <h3>2. Consistency Scores: The Silent Killer</h3>
      <p>Firms like FTMO or MFF look for consistency. If one trade makes 90% of your profit, you are a \"lucky gambler.\" This leads to failed payouts even after hitting profit targets.</p>
      
      <div class="internal-link">
        <span>Mastery Link:</span> <a href="/learn/how-to-review-trades">How to Review Trades Like a Funded Trader</a>
      </div>
    `
  },
  {
    slug: "why-indian-traders-need-journaling",
    title: "Why Indian Traders Need a Dedicated Trading Journal",
    category: "The Indian Trader",
    accent: "#ff9933",
    description: "Moving beyond Telegram signals to professional consistency in Nifty and BankNifty.",
    ctaTitle: "Ready to trade beyond signals?",
    ctaSubtitle: "Build consistency with structured journaling. Own your edge.",
    content: `
      <div class="who-is-this-for">
        <strong>This guide is for traders who:</strong>
        <ul>
          <li>Are tired of the 'Telegram Signal' dependency trap</li>
          <li>Trade NIFTY, BANKNIFTY, or Indian Equities</li>
        </ul>
      </div>

      <h2>The \"Tips & Signals\" Trap</h2>
      <p>Many traders in India start by following Telegram groups. This creates a psychological gap where the trader never learns <em>why</em> a trade worked or failed.</p>
      <div class=\"pro-tip\">
        <strong>The Fix:</strong> A journal shifts power back to you. When you record a trade, you must state your own logic.
      </div>

      <div class="internal-link">
        <span>Related Guide:</span> <a href="/learn/mindset-and-mistake-tracking">Why Mindset Tracking Separates Profitable Traders</a>
      </div>
    `
  },
  {
    slug: "mindset-and-mistake-tracking",
    title: "Why Mindset & Mistake Tracking Separates Profitable Traders",
    category: "Psychology",
    accent: "#a855f7",
    description: "Stop repeating the same errors. Learn to bridge the gap between strategy and execution.",
    ctaTitle: "Master your mental game",
    ctaSubtitle: "Turn your mistakes into your greatest edge. Start tracking today.",
    content: `
      <div class="who-is-this-for">
        <strong>This guide is for traders who:</strong>
        <ul>
          <li>Have a working strategy but fail to follow it</li>
          <li>Suffer from Revenge Trading or FOMO</li>
          <li>Feel like they are their own worst enemy in the markets</li>
        </ul>
      </div>

      <h2>The Execution Gap</h2>
      <p>Most traders think they have a strategy problem. In reality, they have an execution problem. You can have a 70% win-rate strategy, but if you 'fat finger' trades or exit early due to fear, the strategy is useless.</p>

      <h3>1. Categorizing Your Mistakes</h3>
      <p>A loss is not always a mistake. A win is not always a good trade. Professional traders separate "Strategy Losses" from "Execution Mistakes."</p>
      <div class="pro-tip">
        <strong>The Key:</strong> If you followed your rules and lost money, that is a cost of business. If you broke your rules and won money, that is a dangerous mistake that will eventually blow your account.
      </div>

      <h3>2. Tracking Emotional States</h3>
      <p>Are you trading while tired? Angry? Bored? Your journal should record your pre-trade mindset. Over 100 trades, you will likely see a correlation between "Trading while Bored" and "Random Losses."</p>

      <div class="internal-link">
        <span>Deep Dive:</span> <a href="/learn/common-journaling-mistakes">3 Common Journaling Mistakes to Avoid</a>
      </div>
    `
  },
  {
    slug: "how-to-review-trades",
    title: "How to Review Trades Like a Funded Trader",
    category: "Review Process",
    accent: "#10b981",
    description: "The Sunday Routine: How professional traders use their data to scale their size.",
    ctaTitle: "Scale your trading career",
    ctaSubtitle: "Use institutional-grade review processes to grow your capital.",
    content: `
      <div class="who-is-this-for">
        <strong>This guide is for traders who:</strong>
        <ul>
          <li>Have at least 20-30 trades logged</li>
          <li>Want to identify their 'A+ Setup' scientifically</li>
          <li>Are ready to scale their position sizing based on data</li>
        </ul>
      </div>

      <h2>The "Sunday Review" Framework</h2>
      <p>Data entry is only 50% of journaling. The other 50% is the Review. If you aren't looking back at your data, you are just keeping a diary, not a business ledger.</p>

      <h3>1. Identifying Your 'Performance Clusters'</h3>
      <p>Professional traders look for where they make the most money. Is it on Tuesdays? Is it during the London Open? Is it on Mean Reversion trades?</p>
      <div class="pro-tip">
        <strong>Scaling Secret:</strong> Once you identify your highest probability setup (e.g., 'London Breakouts on Tuesdays'), you should double your risk on those and halve your risk on everything else.
      </div>

      <h3>2. The 'MAE' and 'MFE' Metrics</h3>
      <p>To review like a pro, you must track Maximum Adverse Excursion (how much the trade went against you) and Maximum Favorable Excursion (how much it went in your favor before reversing).</p>
      <p>This data tells you if your Stop Losses are too wide or if you are leaving too much money on the table by exiting too early.</p>

      <div class="internal-link">
        <span>Mastery Link:</span> <a href="/learn/prop-firm-trading-journal">Apply this to Prop Firm Evaluations</a>
      </div>
    `
  },
  {
    slug: "common-journaling-mistakes",
    title: "Common Journaling Mistakes Traders Make",
    category: "Diagnostics",
    accent: "#ef4444",
    description: "Avoid the traps that turn journaling into a waste of time.",
    ctaTitle: "Fix your process",
    ctaSubtitle: "Journal smarter, not harder. Join the elite 5%.",
    content: `
      <div class="who-is-this-for">
        <strong>This guide is for traders who:</strong>
        <ul>
          <li>Feel like journaling is a 'chore'</li>
          <li>Aren't seeing improvement despite tracking trades</li>
          <li>Are overwhelmed by too much data</li>
        </ul>
      </div>

      <h2>Mistake #1: Tracking Too Much 'Fluff'</h2>
      <p>Traders often try to track 50 different metrics. This leads to burnout. You don't need to know the moon phase; you need to know if you followed your plan.</p>

      <h2>Mistake #2: The 'Losing Streak' Stop</h2>
      <p>Most traders stop journaling when they start losing. This is the exact moment when journaling is most critical. A journal is your anchor during a drawdown.</p>

      <div class="pro-tip">
        <strong>Pro Tip:</strong> If you stop journaling during a loss, you are hiding from reality. Facing the data is the only way to stop the bleeding.
      </div>

      <h2>Mistake #3: Not Having a 'Why'</h2>
      <p>If your journal doesn't have a "Notes" or "Logic" section, you are just recording numbers. Numbers tell you what happened; notes tell you why it happened.</p>

      <div class="internal-link">
        <span>Psychology Link:</span> <a href="/learn/mindset-and-mistake-tracking">Fix your Mindset Tracking</a>
      </div>
    `
  }
];